import { useState, useEffect, useCallback } from "react";
import styles from "./AdminForm.module.css";
import apiService from "../../../../services/api";

export default function HistoryForm({ item, onSubmit, onCancel }) {
    const [formData, setFormData] = useState({
        year: new Date().getFullYear(),
        lines: [""], // 빈 줄 하나로 시작
        isActive: true,
    });
    const [isSubmitting, setIsSubmitting] = useState(false); // 제출 상태 추가

    useEffect(() => {
        if (item) {
            // 기존 description을 줄 단위로 분리
            const existingLines = item.description
                ? item.description.split("\n").filter((line) => line.trim())
                : [""];
            setFormData({
                year: item.year || new Date().getFullYear(),
                lines: existingLines.length > 0 ? existingLines : [""],
                isActive: item.isActive ?? true,
            });
        }
    }, [item]);

    // 줄 추가
    const addLine = () => {
        setFormData((prev) => ({
            ...prev,
            lines: [...prev.lines, ""],
        }));
    };

    // 줄 제거
    const removeLine = (index) => {
        if (formData.lines.length > 1) {
            const newLines = formData.lines.filter((_, i) => i !== index);
            setFormData((prev) => ({
                ...prev,
                lines: newLines,
            }));
        }
    };

    // 줄 내용 변경
    const updateLine = (index, value) => {
        const newLines = [...formData.lines];
        newLines[index] = value;
        setFormData((prev) => ({
            ...prev,
            lines: newLines,
        }));
    };

    const handleSubmit = useCallback(
        async (e) => {
            e.preventDefault();

            // 중복 제출 방지
            if (isSubmitting) {
                console.log(
                    "Form submission already in progress, ignoring duplicate submit"
                );
                return;
            }

            // 빈 줄 제거하고 description 생성
            const description = formData.lines
                .filter((line) => line.trim())
                .join("\n");

            // API 서버 요구사항에 맞는 데이터 구조 생성
            const apiData = {
                year: formData.year,
                description: description,
                isActive: formData.isActive,
            };

            setIsSubmitting(true);

            try {
                console.log("Submitting history data:", apiData);

                if (item) {
                    await apiService.updateHistory(item.id, apiData);
                } else {
                    await apiService.createHistory(apiData);
                }
                onSubmit();
            } catch (error) {
                console.error("History save error:", error);
                alert("저장에 실패했습니다.");
            } finally {
                setIsSubmitting(false);
            }
        },
        [formData, item, onSubmit, isSubmitting]
    );

    const handleCancel = useCallback(() => {
        if (isSubmitting) return; // 제출 중에는 취소 불가
        onCancel();
    }, [onCancel, isSubmitting]);

    return (
        <form className={styles.formWrap} onSubmit={handleSubmit}>
            <h2 className={styles.title}>{item ? "연혁 수정" : "연혁 등록"}</h2>

            <label className={styles.label}>
                년도
                <input
                    type="number"
                    value={formData.year}
                    onChange={(e) =>
                        setFormData((prev) => ({
                            ...prev,
                            year: parseInt(e.target.value),
                        }))
                    }
                    className={styles.input}
                    required
                    min="1900"
                    max="2100"
                    disabled={isSubmitting}
                />
            </label>

            <label className={styles.label}>
                상세내용
                <div className={styles.linesContainer}>
                    {formData.lines.map((line, index) => (
                        <div key={index} className={styles.lineItem}>
                            <input
                                type="text"
                                value={line}
                                onChange={(e) =>
                                    updateLine(index, e.target.value)
                                }
                                className={styles.input}
                                placeholder={`연혁 내용 ${index + 1}번째 줄`}
                                disabled={isSubmitting}
                            />
                            {formData.lines.length > 1 && (
                                <button
                                    type="button"
                                    onClick={() => removeLine(index)}
                                    className={styles.removeLineBtn}
                                    disabled={isSubmitting}
                                >
                                    삭제
                                </button>
                            )}
                        </div>
                    ))}
                    <button
                        type="button"
                        onClick={addLine}
                        className={styles.addLineBtn}
                        disabled={isSubmitting}
                    >
                        + 줄 추가
                    </button>
                </div>
            </label>

            <div className={styles.buttonGroup}>
                <button
                    type="submit"
                    className={styles.submitBtn}
                    disabled={isSubmitting}
                >
                    {isSubmitting ? "처리중..." : item ? "수정" : "등록"}
                </button>
                <button
                    type="button"
                    onClick={handleCancel}
                    className={styles.cancelBtn}
                    disabled={isSubmitting}
                >
                    취소
                </button>
            </div>
        </form>
    );
}
